package org.teddapp.project.ui.skills

import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.ui.TeddDates
import kotlin.math.ceil

/** Ton d'une date butoir : loin, proche (quatorze jours ou moins), ou dépassée. */
enum class DeadlineTone { Calm, Soon, Past }

/** Une date butoir lue depuis aujourd'hui : l'écart en jours, son libellé, son ton. */
data class DeadlineLabel(val days: Int, val text: String, val tone: DeadlineTone)

/**
 * Ce que l'interface dit d'une date butoir. Rien ici n'est une règle du noyau : la
 * date est une donnée de la compétence, le noyau ne la connaît pas. C'est un calcul
 * d'affichage, le même sur la fiche, la vue d'ensemble et l'écran Aujourd'hui.
 */
object SkillDeadline {
    /** À partir de combien de jours la date se rappelle à l'écran Aujourd'hui. */
    const val SOON_DAYS = 14

    /** Libellé « J-12 », « aujourd'hui » ou « dépassée de 3 j » ; null sans date valable. */
    fun label(deadline: String?, today: String = TODAY_ISO): DeadlineLabel? {
        val target = TeddDates.daysFromIso(deadline) ?: return null
        val now = TeddDates.daysFromIso(today) ?: return null
        val days = target - now
        val text = when {
            days > 0 -> "J-$days"
            days == 0 -> "aujourd'hui"
            else -> "dépassée de ${-days} j"
        }
        val tone = when {
            days < 0 -> DeadlineTone.Past
            days <= SOON_DAYS -> DeadlineTone.Soon
            else -> DeadlineTone.Calm
        }
        return DeadlineLabel(days, text, tone)
    }

    /** Vrai quand la date mérite un rappel sur Aujourd'hui : proche ou dépassée. */
    fun needsReminder(label: DeadlineLabel): Boolean = label.tone != DeadlineTone.Calm

    /**
     * Le rappel, avec le rythme à tenir quand on connaît les heures restantes :
     * « J-12 · 80 h restantes, 7 h par jour ». Division d'affichage, rien de plus.
     */
    fun reminder(label: DeadlineLabel, hoursLeft: Double?): String {
        if (hoursLeft == null) return label.text
        if (hoursLeft <= 0.0) return "${label.text} · cible atteinte"
        val left = "${formatHours(hoursLeft)} restantes"
        if (label.days <= 0) return "${label.text} · $left"
        return "${label.text} · $left, ${formatHours(hoursLeft / label.days)} par jour"
    }

    /** « 80 h » ou « 6,5 h » : une décimale seulement quand elle change quelque chose. */
    fun formatHours(hours: Double): String {
        val tenths = ceil(hours * 10).toLong()
        return if (tenths % 10 == 0L) "${tenths / 10} h" else "${tenths / 10},${tenths % 10} h"
    }
}
